import { useEffect, useRef } from "react";
import Hls from "hls.js";
import { RadioTower } from "lucide-react";
import type { Channel, PlaybackSession } from "../../shared/types";
import { createHlsConfig } from "../lib/player-config";

interface PlayerProps {
  channel?: Channel;
  session?: PlaybackSession;
  starting: boolean;
  onFatal: () => void;
  onPlaybackEvent: (message: string) => void;
}

export function Player({ channel, session, starting, onFatal, onPlaybackEvent }: PlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const fatalRecoveries = useRef(0);
  const recovering = useRef(false);
  const savedPosition = useRef(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !session) {
      return;
    }

    fatalRecoveries.current = 0;
    recovering.current = false;
    savedPosition.current = 0;
    let stallRecoveryCount = 0;
    let manifestCount = 0;
    const source = session.playlistUrl;
    let hls: Hls | undefined;
    const requestPlayback = (message: string) => {
      onPlaybackEvent(message);
      void video.play().catch(() => onPlaybackEvent("Waiting for playback permission"));
    };

    const doRecover = () => {
      // Save position before recovery so we can restore after manifest reload
      savedPosition.current = video?.currentTime ?? 0;
      recovering.current = true;
      hls?.startLoad();
    };

    if (Hls.isSupported()) {
      hls = new Hls(createHlsConfig());

      hls.attachMedia(video);
      hls.loadSource(source);
      requestPlayback("Preparing delayed buffer");
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        manifestCount += 1;
        // On initial load, just play. On recovery, seek back to saved position.
        if (recovering.current && savedPosition.current > 0 && video) {
          recovering.current = false;
          onPlaybackEvent("Playlist restored");
          // Seek back to where we were before the network hiccup
          if (Number.isFinite(savedPosition.current)) {
            video.currentTime = savedPosition.current;
          }
          savedPosition.current = 0;
          void video.play().catch(() => undefined);
          return;
        }
        requestPlayback("Proxy playlist ready");
      });
      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (!data.fatal) {
          onPlaybackEvent(String(data.details ?? "Recovering"));
          return;
        }

        if (data.details === "bufferStalledError") {
          stallRecoveryCount += 1;
          if (stallRecoveryCount >= 2) {
            onPlaybackEvent("Buffer stalled — restarting session");
            fatalRecoveries.current += 1;
            if (fatalRecoveries.current >= 1) {
              onFatal();
            }
            return;
          }
          onPlaybackEvent("Recovering stalled buffer");
          // Don't jump on stall recovery — position is still valid
          hls?.startLoad();
          return;
        }

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          // Debounce: if already recovering, skip duplicate startLoad
          if (recovering.current) {
            onPlaybackEvent("Awaiting manifest reload");
            return;
          }
          onPlaybackEvent("Recovering network");
          doRecover();
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          onPlaybackEvent("Recovering media");
          hls?.recoverMediaError();
          return;
        }

        fatalRecoveries.current += 1;
        onPlaybackEvent("Restarting session");
        if (fatalRecoveries.current >= 1) {
          onFatal();
        }
      });
    } else if (video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = source;
      requestPlayback("Preparing delayed buffer");
    } else {
      onPlaybackEvent("HLS unsupported");
    }

    return () => {
      hls?.destroy();
      video.removeAttribute("src");
      video.load();
    };
  }, [onFatal, onPlaybackEvent, session]);

  return (
    <div className="video-shell">
      <div className="video-title">
        <div>
          <span>{channel?.group ?? "No channel"}</span>
          <h2>{channel?.name ?? "Select a channel"}</h2>
        </div>
        {starting ? <strong>Opening</strong> : null}
      </div>

      {session ? (
        <video ref={videoRef} className="video-player" controls playsInline autoPlay />
      ) : (
        <div className="empty-player">
          <RadioTower aria-hidden="true" size={42} />
          <span>{channel ? "Start the selected channel" : "Select a channel"}</span>
        </div>
      )}
    </div>
  );
}
